import { hasPermission } from "../auth.js";
import { openDialog } from "../../components/features/app-dialog/utils/core/dialogManager.js";
import { apiFetch } from "../apiClient.js";
import { employmentTypes } from "../utils/enums.js";
import { deepLink, isResolvedName } from "../utils/deepLink.js";
import { formatDateISO } from "../../components/shared/app-input/utils/dateUtils.js";
import { formatTimeSpan } from "../../components/features/app-table/tableManager/timeLogManager.js";
import { timeToHours } from "../../components/features/app-table/utils/timeUtils.js";
import { computePeriodRange, shiftRefDate } from "../../components/shared/app-filter/utils/periodRange.js";
import { createPeriodPopup, renderWeekPicker, renderMonthPicker, renderRangePicker } from "../../components/shared/app-filter/utils/periodPicker.js";
import { fmtDE } from "../../components/shared/app-input/utils/dateUtils.js";

function formatHours(decimal) {
    const total = Math.round(Number(decimal) * 60);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function formatSignedHours(decimal) {
    const total = Math.round(Number(decimal) * 60);
    const sign = total < 0 ? '-' : '+';
    const abs = Math.abs(total);
    return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
}

function formatSignedDays(decimal) {
    const sign = decimal < 0 ? '-' : '+';
    return `${sign}${Math.abs(decimal).toFixed(2)}`;
}

function formatEmploymentType(value) {
    return value != null ? (employmentTypes[value] ?? '—') : '—';
}

const WERKSTUDENT_EMPLOYMENT_TYPE = 2; // see app/utils/enums.js employmentTypes
const WERKSTUDENT_WEEKLY_LIMIT_HOURS = 20;

const periodStateByUser = new Map();
let currentPeriodPopup = null;

function mondayKey(dateStr) {
    const date = new Date(`${dateStr}T00:00:00`);
    const day = date.getDay();
    date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
    return formatDateISO(date);
}

function parseHashQuery() {
    return new URLSearchParams(window.location.hash.split('?')[1] || '');
}

const PERIOD_MODES = ['week', 'month', 'year', 'custom'];

function maxWeeklyHours(workDays) {
    const totals = {};
    workDays.forEach(w => {
        if (!w.date) return;
        const key = mondayKey(w.date);
        totals[key] = (totals[key] || 0) + timeToHours(formatTimeSpan(w.duration));
    });
    return Math.max(0, ...Object.values(totals));
}

async function fetchPeriodStats(userId, from, to) {
    const [entriesRaw, workDaysRaw, detailRaw] = await Promise.all([
        apiFetch(`Entries?userId=${userId}&from=${from}&to=${to}`, 'GET', undefined, { silent: true }),
        apiFetch(`WorkDay?userId=${userId}&from=${from}&to=${to}`, 'GET', undefined, { silent: true }),
        apiFetch(`Users/${userId}?from=${from}&to=${to}`, 'GET', undefined, { silent: true }),
    ]);

    const entries = (Array.isArray(entriesRaw) ? entriesRaw : []).filter(e => (e.userId == null || e.userId === userId) && e.workedHours != null);
    const workDays = (Array.isArray(workDaysRaw) ? workDaysRaw : []).filter(w => w.userId == null || w.userId === userId);

    const totalEntryHours = entries.reduce((sum, e) => sum + (e.workedHours || 0), 0);
    const totalWorkHours = workDays.reduce((sum, w) => sum + timeToHours(formatTimeSpan(w.duration)), 0);

    return {
        saldoHours: detailRaw?.workDays?.saldoHours ?? 0,
        entries: {
            totalHours: totalEntryHours,
            avgHours: entries.length ? totalEntryHours / entries.length : 0,
            count: entries.length,
        },
        workDays: {
            totalHours: totalWorkHours,
            avgHours: workDays.length ? totalWorkHours / workDays.length : 0,
            count: workDays.length,
            maxWeeklyHours: maxWeeklyHours(workDays),
        },
    };
}

export async function mountUsersView(content, { selectedId = null } = {}) {
    const container = document.createElement("div");
    container.id = "users-view";

    const list = document.createElement("app-panel-list");
    const detail = document.createElement("app-panel-detail");

    container.appendChild(list);
    container.appendChild(detail);
    content.appendChild(container);

    const getListName = user => user.name;

    function buildStammdatenCards(data) {
        return [
            { title: 'STAMMDATEN', rows: [
                { label: 'Email', value: data?.user?.email },
                {
                    label: 'Rolle', value: data?.user?.roleName,
                    onRowClick: deepLink('Roles.Read', '#roles'),
                    onValueClick: deepLink('Roles.Read', `#roles?col-name=${encodeURIComponent(data?.user?.roleName ?? '')}`, isResolvedName(data?.user?.roleName)),
                },
                {
                    label: 'Team', value: data?.user?.teamName,
                    onRowClick: deepLink('Teams.Read', '#teams'),
                    onValueClick: deepLink('Teams.Read', `#teams?col-name=${encodeURIComponent(data?.user?.teamName ?? '')}`, isResolvedName(data?.user?.teamName)),
                },
                { label: 'Beschäftigungsverhältnis', value: formatEmploymentType(data?.user?.employmentType) }
            ], wide: true, directEdit: hasPermission('Users.Update') ? { handler: () => openDialog('editUser', detail.currentData?.user) } : undefined },
        ];
    }

    function syncUrl(userId) {
        if (userId == null) return;
        const params = new URLSearchParams();
        const state = periodStateByUser.get(userId);
        if (state) {
            params.set('mode', state.mode);
            if (state.mode === 'custom') {
                if (state.from) params.set('from', state.from);
                if (state.to) params.set('to', state.to);
            } else if (state.refDate) {
                params.set('ref', formatDateISO(state.refDate));
            }
        }
        const query = params.toString();
        history.replaceState({}, '', `#users/${userId}${query ? `?${query}` : ''}`);
    }

    function buildZusammenfassungCards(stats) {
        const isStudent = detail.currentData?.user?.employmentType === WERKSTUDENT_EMPLOYMENT_TYPE;
        const loading = stats == null;
        const exceeded = isStudent && !loading && stats.workDays.maxWeeklyHours > WERKSTUDENT_WEEKLY_LIMIT_HOURS;
        const name = encodeURIComponent(getListName(detail.currentItem ?? {}));
        const saldoHours = loading ? 0 : stats.saldoHours;
        const saldoDays = saldoHours / 8;

        return [
            ...(isStudent ? [] : [{ title: 'SALDO', wide: true, stats: [
                { value: loading ? '--:--' : formatSignedHours(saldoHours), label: 'Stunden' },
                { value: loading ? '—' : formatSignedDays(saldoDays),   label: 'Tage' },
            ] }]),
            { title: 'LEISTUNGSNACHWEISE', stats: [
                { value: loading ? '--:--' : formatHours(stats.entries.totalHours), label: 'Stunden gesamt' },
                { value: loading ? '--:--' : formatHours(stats.entries.avgHours),   label: 'Durchschnitt' },
                { value: loading ? '—' : stats.entries.count,                       label: 'Einträge' },
            ], onClick: deepLink('Entries.Read.Any', `#entries?scope=all&col-userId=${detail.currentItem?.id}&colName-userId=${name}`) },
            { title: 'ARBEITSTAGE', stats: [
                { value: loading ? '--:--' : formatHours(stats.workDays.totalHours), label: 'Arbeitszeit gesamt' },
                { value: loading ? '--:--' : formatHours(stats.workDays.avgHours),   label: 'Durchschnitt' },
                { value: loading ? '—' : stats.workDays.count,                       label: 'Protokolle' },
            ], onClick: deepLink('Timelogs.Read.Any', `#timelogs?scope=all&col-memberId=${detail.currentItem?.id}&colName-memberId=${name}`) },
            ...(exceeded ? [{ title: 'WARNUNG', wide: true, warn: true, rows: [
                { label: 'Über 20 Std./Woche gearbeitet', value: formatHours(stats.workDays.maxWeeklyHours) },
            ] }] : []),
        ];
    }

    function buildAllCards(stats) {
        return [
            { headerSlot: true },
            ...buildZusammenfassungCards(stats),
            { divider: true },
            ...buildStammdatenCards(detail.currentData),
        ];
    }

    function mountSummaryFilter(container, { updateCards }) {
        currentPeriodPopup?.close();
        container.innerHTML = '';

        const userId = detail.currentItem?.id;
        if (userId == null) return;

        let state = periodStateByUser.get(userId);
        if (!state) {
            state = { mode: 'month', refDate: new Date(), from: null, to: null, custom: null, stats: null };
            periodStateByUser.set(userId, state);
        }

        const popup = createPeriodPopup(() => container.getRootNode());
        currentPeriodPopup = popup;

        container.innerHTML = `
            <div class="filter-row">
                <div class="period-mode">
                    <div class="period-mode-slider" id="periodSlider"></div>
                    <button class="period-mode-btn" data-mode="week">Woche</button>
                    <button class="period-mode-btn" data-mode="month">Monat</button>
                    <button class="period-mode-btn" data-mode="year">Jahr</button>
                    <button class="period-mode-btn" data-mode="custom">Zeitraum</button>
                </div>
                <div class="period-nav" id="periodNav"></div>
            </div>
        `;

        const modeButtons = [...container.querySelectorAll('.period-mode-btn')];
        const slider = container.querySelector('#periodSlider');
        const navEl = container.querySelector('#periodNav');

        const isStale = () => detail.currentItem?.id !== userId;

        function moveSlider(animate = true) {
            const activeBtn = modeButtons.find(b => b.classList.contains('active'));
            if (!slider || !activeBtn || !activeBtn.offsetWidth) return;
            slider.style.transition = animate ? 'transform 0.2s ease, width 0.2s ease' : 'none';
            slider.style.width = `${activeBtn.offsetWidth}px`;
            slider.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
        }

        async function refresh() {
            if (!state.from || !state.to) {
                updateCards(buildAllCards(null));
                return;
            }
            if (state.stats) {
                updateCards(buildAllCards(state.stats));
                return;
            }
            updateCards(buildAllCards(null));
            const stats = await fetchPeriodStats(userId, state.from, state.to);
            if (isStale()) return;
            state.stats = stats;
            updateCards(buildAllCards(stats));
        }

        function renderNav() {
            popup.close();

            if (state.mode === 'custom') {
                const label = (state.custom?.from && state.custom?.to)
                    ? `${fmtDE(state.custom.from)} – ${fmtDE(state.custom.to)}`
                    : 'Zeitraum wählen';
                navEl.innerHTML = `<button class="period-label period-label-custom" id="periodLabelBtn" type="button">${label}</button>`;
                const labelBtn = navEl.querySelector('#periodLabelBtn');
                labelBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    popup.open(labelBtn, panel => renderRangePicker(panel, state.custom, state.refDate, (from, to) => {
                        state.custom = { from, to };
                        state.from = from;
                        state.to = to;
                        state.stats = null;
                        popup.close();
                        renderNav();
                        syncUrl(userId);
                        refresh();
                    }));
                });
                return;
            }

            const { from, to, label } = computePeriodRange(state.mode, state.refDate);
            state.from = from;
            state.to = to;

            const pickable = state.mode === 'week' || state.mode === 'month';
            const labelHtml = pickable
                ? `<button class="period-label" id="periodLabelBtn" type="button">${label}</button>`
                : `<span class="period-label">${label}</span>`;
            navEl.innerHTML = `
                <button class="period-nav-btn" id="periodPrev" title="Vorheriger Zeitraum"><i class="fa-solid fa-chevron-left"></i></button>
                ${labelHtml}
                <button class="period-nav-btn" id="periodNext" title="Nächster Zeitraum"><i class="fa-solid fa-chevron-right"></i></button>
            `;
            navEl.querySelector('#periodPrev').addEventListener('click', () => {
                state.refDate = shiftRefDate(state.mode, state.refDate, -1);
                state.stats = null;
                renderNav();
                syncUrl(userId);
                refresh();
            });
            navEl.querySelector('#periodNext').addEventListener('click', () => {
                state.refDate = shiftRefDate(state.mode, state.refDate, 1);
                state.stats = null;
                renderNav();
                syncUrl(userId);
                refresh();
            });

            const labelBtn = navEl.querySelector('#periodLabelBtn');
            if (labelBtn) {
                labelBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (state.mode === 'week') {
                        popup.open(labelBtn, panel => renderWeekPicker(panel, state.refDate, (monday) => {
                            state.refDate = monday;
                            state.stats = null;
                            popup.close();
                            renderNav();
                            syncUrl(userId);
                            refresh();
                        }));
                    } else if (state.mode === 'month') {
                        popup.open(labelBtn, panel => renderMonthPicker(panel, state.refDate, (monthIndex) => {
                            state.refDate = new Date(state.refDate.getFullYear(), monthIndex, 1);
                            state.stats = null;
                            popup.close();
                            renderNav();
                            syncUrl(userId);
                            refresh();
                        }));
                    }
                });
            }
        }

        modeButtons.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.mode === state.mode);
            btn.addEventListener('click', () => {
                if (state.mode === btn.dataset.mode) return;
                state.mode = btn.dataset.mode;
                state.stats = null;
                modeButtons.forEach(b => b.classList.toggle('active', b === btn));
                moveSlider();
                renderNav();
                syncUrl(userId);
                requestAnimationFrame(() => refresh());
            });
        });

        renderNav();
        requestAnimationFrame(() => moveSlider(false));
        refresh();
    }

    list.configure({
        title: 'BENUTZER',
        endpoint: 'Users',
        getName: getListName,
        getSubtitle: user => user.teamName ?? '—',
        dataChangedEvent: 'user-data-changed',
        initialSelectedId: selectedId,
        onSelect: (item) => {
            const params = parseHashQuery();

            if (!periodStateByUser.has(item.id)) {
                const mode = PERIOD_MODES.includes(params.get('mode')) ? params.get('mode') : 'week';
                const ref = params.get('ref');
                const from = params.get('from');
                const to = params.get('to');
                const state = { mode, refDate: ref ? new Date(`${ref}T00:00:00`) : new Date(), from: null, to: null, custom: null, stats: null };
                if (mode === 'custom' && from && to) {
                    state.custom = { from, to };
                    state.from = from;
                    state.to = to;
                }
                periodStateByUser.set(item.id, state);
            }

            detail.load(item);
            syncUrl(item.id);
        },
        onDeselect: () => {
            detail.clear();
            const query = window.location.hash.split('?')[1];
            history.replaceState({}, '', `#users${query ? `?${query}` : ''}`);
        },
        actions: [
            {
                label: 'Als Vorlage verwenden', icon: 'fa-copy',
                permission: () => hasPermission('Entries.Create'),
                handler: async (item) => {
                    const user = await apiFetch(`Users/${item.id}`);
                    openDialog('createUser', user?.user ?? item);
                },
            },
            {
                label: 'Löschen', icon: 'fa-trash', danger: true,
                permission: () => hasPermission('Users.Delete'),
                handler: (item) => openDialog('deleteUser', item),
            },
        ],
        onDataChanged: (listEl, event) => {
            const { action, user } = event.detail ?? {};
            if (action === 'created' && user?.user?.id) {
                listEl.addItem(user.user);
                listEl.selectItem(user.user.id, { silent: true });
                listEl.flashItem(user.user.id, 'create');
            } else if (action === 'updated' && user?.user?.id) {
                listEl.updateItem(user.user.id, item => ({ ...item, name: user.user.name, teamName: user.user.teamName }));
                listEl.flashItem(user.user.id, 'update');
            }
        },
    });

    detail.configure({
        emptyText: 'Benutzer auswählen',
        subLabel: 'Benutzer',
        getName: data => data?.user?.name ?? data?.user?.email,
        getAvatarStr: getListName,
        canEdit: () => false,
        canDelete: () => false,
        loadData: async (user) => await apiFetch(`Users/${user.id}`) ?? { user, entries: {}, workDays: {} },
        renderCards: () => {
            const userId = detail.currentItem?.id;
            const stats = userId != null ? periodStateByUser.get(userId)?.stats ?? null : null;
            return buildAllCards(stats);
        },
        mountHeader: (container, data, api) => mountSummaryFilter(container, api),
        dataChangedEvent: 'user-data-changed',
        onDataChanged: (detail, event) => {
            const { action, user, id } = event.detail ?? {};
            if (action === 'created' && user?.user?.id) {
                detail.setData(user, user.user);
                return;
            }
            if (!detail.currentItem) return;
            if (action === 'deleted' && id === detail.currentItem.id) detail.clear();
            else if (action === 'updated' && user?.user?.id === detail.currentItem.id) detail.setData(user);
        },
    });
}
